import {
  ArrowTopRightOnSquareIcon,
  MapPinIcon,
} from "@heroicons/react/24/outline";
import { Link } from "@tanstack/react-router";
import { cn } from "helpers/classNames";
import { ComponentType, ReactNode } from "react";
import { Facility } from "types/facility.type";

type DetailTileProps = {
  icon: ComponentType<{ className?: string }>;
  label: string;
  value?: ReactNode;
  /** Utan onClick renderas en ren info-ruta i stället för en knapp */
  onClick?: () => void;
  className?: string;
};

export const DetailTile = ({
  icon: Icon,
  label,
  value,
  onClick,
  className,
}: DetailTileProps) => {
  const content = (
    <>
      <span className="flex items-center gap-x-1.5 text-xs font-semibold uppercase tracking-wide text-gray-500">
        <Icon className="h-4 w-4 shrink-0" />
        {label}
      </span>
      {value ? (
        <span className="break-words text-sm font-semibold text-gray-900">
          {value}
        </span>
      ) : onClick ? (
        <span className="text-sm font-semibold text-blue-700">+ Fyll i</span>
      ) : (
        <span className="text-sm font-semibold text-gray-300">–</span>
      )}
    </>
  );

  if (!onClick) {
    return (
      <div
        className={cn(
          "flex flex-col items-start gap-y-1 rounded-xl border border-gray-75 bg-white p-3 text-left",
          className,
        )}
      >
        {content}
      </div>
    );
  }

  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        "flex flex-col items-start gap-y-1 rounded-xl border p-3 text-left transition-colors",
        value
          ? "border-gray-75 bg-white active:bg-gray-12"
          : "border-dashed border-blue-300 bg-blue-50/40 active:bg-blue-50",
        className,
      )}
    >
      {content}
    </button>
  );
};

type FacilityTileProps = {
  facility: Facility;
  label?: string;
};

export const FacilityTile = ({
  facility,
  label = "Hämtställe",
}: FacilityTileProps) => (
  <Link
    to="/app/kunder/$customerId/facility/$facilityId"
    params={{
      customerId: facility.customer.id.toString(),
      facilityId: facility.id.toString(),
    }}
    className="flex flex-col items-start gap-y-1 rounded-xl border border-gray-75 bg-white p-3 text-left transition-colors active:bg-gray-12"
  >
    <span className="flex w-full items-center gap-x-1.5 text-xs font-semibold uppercase tracking-wide text-gray-500">
      <MapPinIcon className="h-4 w-4 shrink-0" />
      {label}
      <ArrowTopRightOnSquareIcon
        className="ml-auto h-4 w-4 shrink-0 text-gray-400"
        aria-hidden="true"
      />
    </span>
    <span className="break-words text-sm font-semibold text-gray-900">
      {facility.name}
    </span>
    {!!facility.address && (
      <span className="text-xs text-gray-500">
        {facility.address}
        {facility.city ? `, ${facility.city}` : ""}
      </span>
    )}
  </Link>
);
